import { useEffect, useMemo, useState } from "react";
import { Scheduling } from "../models/schedule";
import { fetchConsultantSchedulings, fetchFleetSchedulings } from "../api/fetchScheduling";
import { debounce } from "lodash";

function useSchedulingList(userType: string) {
    const [lastKey, setLastKey] = useState<string | null>();
    const [sortDirection, setSortDirection] = useState<string>("asc");
    const [queryChassis, setQueryChassis] = useState<string>("");
    const [date, setDate] = useState<moment.Moment | null>(null);
    const [datePickerKey, setDatePickerKey] = useState<number>(0);
    
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMoreData, setHasMoreData] = useState<boolean>(true);

    const [schedulings, setSchedulings] = useState<Scheduling[]>([]);

    useEffect(() => {
        setLastKey(null);
        // Limpar apenas quando os filtros mudarem (nova busca)
        // Mas limpar apenas após receber resposta, não antes
        getSchedulings(null, []);
    }, [sortDirection, queryChassis, date]);

    const searchByChassis = useMemo(() => debounce(async (searchTerm: string) => setQueryChassis(searchTerm), 500), []);

    const getSchedulings = async (localLastKey?: string | null, localSchedulings?: Scheduling[]) => {
        setLoading(true);
        try {
            const effectiveLastKey = localLastKey === undefined ? lastKey : localLastKey;
            const data = userType === 'dealership' ?
                await fetchConsultantSchedulings(effectiveLastKey, sortDirection, queryChassis, date?.format("YYYY-MM-DD")) :
                await fetchFleetSchedulings(effectiveLastKey, sortDirection, queryChassis, date?.format("YYYY-MM-DD"));

            // Só atualizar se recebeu dados válidos
            if (data && data.items && Array.isArray(data.items)) {
                // Se localSchedulings foi passado como [], significa que é uma nova busca (reset)
                // Caso contrário, é um "carregar mais"
                if (localSchedulings !== undefined) {
                    // Nova busca - substituir dados existentes
                    setSchedulings(data.items);
                } else {
                    // Carregar mais - adicionar aos existentes
                    setSchedulings([...schedulings, ...data.items]);
                }
                setLastKey(data.lastKey);
                setHasMoreData(!(data.items.length < 20));
            } else {
                // Se não recebeu dados válidos e é uma nova busca, limpar
                if (localSchedulings !== undefined) {
                    setSchedulings([]);
                }
                console.warn("No valid data received from fetchSchedulings:", data);
            }
        } catch (error) {
            console.error("Error fetching schedulings:", error);
            // Em caso de erro, não limpar os dados existentes se já houver
            // Se for uma nova busca (localSchedulings === []), já foi limpo no useEffect
            // Se for "carregar mais", manter os dados existentes
            if (localSchedulings === undefined) {
                // É um "carregar mais" que falhou - manter dados existentes
                console.warn("Failed to load more schedulings, keeping existing data");
            }
            // Se localSchedulings === [], é uma nova busca que falhou - já está vazio
        } finally {
            setLoading(false);
        }
    }

    const clearDateFilter = () => {
        setDate(null);
        setDatePickerKey((prevKey) => prevKey + 1);
    }

    return { 
        schedulings, getSchedulings,
        sortDirection, setSortDirection, 
        searchByChassis, 
        date, setDate, datePickerKey, clearDateFilter, 
        loading, hasMoreData 
    }
}

export default useSchedulingList;